import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';

interface IndustryMenuProps {
  className?: string;
  onItemClick?: () => void;
  variant?: 'dropdown' | 'horizontal' | 'carousel';
}

const IndustryMenu: React.FC<IndustryMenuProps> = ({ className, onItemClick, variant = 'dropdown' }) => {
  const { industryPages, isIndustryActive, getIndustryPath } = useNavigationUtils();
  const { currentLanguage } = useLanguage();
  
  const getIndustryTitle = (industry: string) => {
    const titles: {[key: string]: {[key: string]: string}} = {
      'finance': {
        'de': 'Finanzsektor',
        'en': 'Finance',
        'fr': 'Finance',
        'it': 'Finanza'
      },
      'luxury': {
        'de': 'Luxusmarken',
        'en': 'Luxury',
        'fr': 'Luxe',
        'it': 'Lusso'
      },
      'tourism': {
        'de': 'Tourismus',
        'en': 'Tourism',
        'fr': 'Tourisme',
        'it': 'Turismo'
      },
      'healthcare': {
        'de': 'Gesundheitswesen',
        'en': 'Healthcare',
        'fr': 'Santé',
        'it': 'Sanità'
      }
    };
    
    return titles[industry]?.[currentLanguage] || industry;
  };

  if (variant === 'horizontal') {
    // Horizontal sliding marquee menu
    return (
      <div className={cn("w-full overflow-hidden", className)}>
        <div className="flex space-x-8 animate-marquee whitespace-nowrap">
          {industryPages.concat(industryPages).map((industry, index) => (
            <Link 
              key={`${industry.key}-${index}`}
              to={getIndustryPath(industry.key)}
              className={cn(
                "flex items-center px-4 py-2 rounded-full transition-colors",
                isIndustryActive(industry.key) ? 
                  "bg-purple-700/40 text-white font-medium border border-purple-500/40" : 
                  "text-gray-300 hover:text-white hover:bg-purple-900/20"
              )}
              onClick={onItemClick}
            >
              <span>{getIndustryTitle(industry.key)}</span>
              <ChevronRight className="w-4 h-4 ml-1.5 text-purple-400" />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'carousel') {
    // Carousel-style menu
    return (
      <div className={cn("w-full py-3 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/95 to-[#0A0A0A] border-t border-b border-purple-500/10", className)}>
        <div className="container mx-auto">
          <div className="flex items-center justify-between gap-4 overflow-x-auto hide-scrollbar px-4 py-1">
            {industryPages.map((industry) => (
              <Link 
                key={industry.key}
                to={getIndustryPath(industry.key)}
                className={cn(
                  "flex items-center transition-all px-4 py-2 rounded-md whitespace-nowrap",
                  isIndustryActive(industry.key) ? 
                    "bg-purple-900/30 text-white font-medium border border-purple-500/40" : 
                    "text-gray-300 hover:text-white hover:bg-purple-900/20"
                )}
                onClick={onItemClick}
              >
                <span>{getIndustryTitle(industry.key)}</span>
              </Link>
            ))}
            <Link 
              to={`/${currentLanguage}/${
                currentLanguage === 'de' ? 'ki-videomarketing-vs-traditionelle-produktion' : 
                currentLanguage === 'en' ? 'ai-video-marketing-vs-traditional-production' : 
                currentLanguage === 'fr' ? 'video-marketing-ia-vs-production-traditionnelle' : 
                'marketing-video-ia-vs-produzione-tradizionale'
              }`}
              className="flex items-center px-4 py-2 text-sm rounded-md whitespace-nowrap text-purple-300 hover:text-white hover:bg-purple-900/20 transition-all"
              onClick={onItemClick}
            >
              <span>
                {currentLanguage === 'de' ? 'KI vs. Traditionell' : 
                 currentLanguage === 'en' ? 'AI vs. Traditional' :
                 currentLanguage === 'fr' ? 'IA vs. Traditionnel' :
                 'AI vs. Tradizionale'}
              </span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Default dropdown menu - now only used for reference, the component has been removed from the UI
  return (
    <div className={cn("rounded-lg backdrop-blur-md bg-[#1A1F2C]/95 border border-purple-700/20 shadow-lg shadow-purple-900/20", className)}>
      <div className="px-4 py-3 border-b border-purple-500/20">
        <span className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
          {currentLanguage === 'de' ? 'Branchen' : 
           currentLanguage === 'en' ? 'Industries' :
           currentLanguage === 'fr' ? 'Industries' :
           'Industrie'}
        </span>
      </div>
      
      <div className="p-2">
        {industryPages.map((industry) => (
          <Link 
            key={industry.key}
            to={getIndustryPath(industry.key)}
            className={cn(
              "flex items-center px-4 py-2.5 text-sm hover:bg-purple-900/20 transition-colors rounded-md",
              isIndustryActive(industry.key) ? 
                "bg-purple-700/30 text-white" : 
                "text-gray-200"
            )}
            onClick={onItemClick}
          >
            <span className="mr-1.5 text-purple-400">•</span>
            <span>{getIndustryTitle(industry.key)}</span>
          </Link>
        ))}
        
        <div className="mt-1 pt-2 border-t border-purple-500/20">
          <Link 
            to={`/${currentLanguage}/${
              currentLanguage === 'de' ? 'ki-videomarketing-vs-traditionelle-produktion' : 
              currentLanguage === 'en' ? 'ai-video-marketing-vs-traditional-production' : 
              currentLanguage === 'fr' ? 'video-marketing-ia-vs-production-traditionnelle' : 
              'marketing-video-ia-vs-produzione-tradizionale'
            }`}
            className="flex items-center px-4 py-2.5 text-sm hover:bg-purple-900/20 transition-colors rounded-md text-gray-200"
            onClick={onItemClick}
          >
            <span className="mr-1.5 text-purple-400">↔</span>
            <span>
              {currentLanguage === 'de' ? 'KI vs. Traditionelle Videos' : 
               currentLanguage === 'en' ? 'AI vs. Traditional Videos' :
               currentLanguage === 'fr' ? 'IA vs. Vidéos Traditionnelles' :
               'AI vs. Video Tradizionali'}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default IndustryMenu;
